/**
 * Tech tree panel — displays three cultivation stage columns with unlocked buildings,
 * current stage highlight, research button, and progress bar.
 * Toggled with T key.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { GameWebSocketClient } from './network_websocket_client';
import { GameClientWorldState, BUILDING_UNLOCK_STAGE } from './game_client_world_state';
import { TechStage, BuildingKind } from './client_message_protocol';

export const PANEL_WIDTH = 900;
export const PANEL_HEIGHT = 550;
const BG_COLOR = 0x000000;
const BG_ALPHA = 0.85;
const TEXT_COLOR = 0xdddddd;
const ACCENT_COLOR = 0x44ff44;
const GOLD_COLOR = 0xffcc44;
const DIM_COLOR = 0x666666;
const HIGHLIGHT_GREEN = 0x226622;
const CURRENT_BORDER = 0x44ff44;
const RESEARCH_COST: Record<string, number> = {
  'GoldenCore': 500,
  'NascentSoul': 2000,
};

const TECH_LABELS: Record<string, string> = {
  'Foundation': '筑基',
  'GoldenCore': '金丹',
  'NascentSoul': '元婴',
};

const TECH_ORDER: string[] = ['Foundation', 'GoldenCore', 'NascentSoul'];

const BUILD_LABELS: Record<BuildingKind, string> = {
  [BuildingKind.SpiritRoot]: '灵根',
  [BuildingKind.GatheringArray]: '采集阵',
  [BuildingKind.RefiningArray]: '精炼阵',
  [BuildingKind.AlchemyFurnace]: '丹炉',
  [BuildingKind.Forge]: '锻造台',
  [BuildingKind.PuppetWorkshop]: '傀儡坊',
  [BuildingKind.DefenseTower]: '防御塔',
  [BuildingKind.SwordTower]: '剑塔',
  [BuildingKind.SlowTrap]: '减速陷阱',
  [BuildingKind.PuppetBarracks]: '傀儡营',
  [BuildingKind.TransporterStation]: '搬运站',
  [BuildingKind.HerbFarm]: '药田',
};

/** Buildings unlocked at each stage (hardcoded, same as BUILDING_UNLOCK_STAGE) */
const STAGE_BUILDINGS: Record<string, BuildingKind[]> = {
  'Foundation': [
    BuildingKind.SpiritRoot,
    BuildingKind.GatheringArray,
    BuildingKind.TransporterStation,
    BuildingKind.HerbFarm,
  ],
  'GoldenCore': [
    BuildingKind.RefiningArray,
    BuildingKind.AlchemyFurnace,
    BuildingKind.Forge,
    BuildingKind.SwordTower,
    BuildingKind.SlowTrap,
  ],
  'NascentSoul': [
    BuildingKind.PuppetWorkshop,
    BuildingKind.DefenseTower,
    BuildingKind.PuppetBarracks,
  ],
};

const COLUMN_WIDTH = (PANEL_WIDTH - 80) / 3; // ~193px per column

export class UiTechTreePanel {
  container: Container;
  visible = false;
  private bg: Graphics;
  private titleText: Text;
  private stoneText: Text;
  private researchButton: Container;
  private researchButtonText: Text;
  private progressBarBg: Graphics;
  private progressBarFill: Graphics;
  private progressText: Text;
  private wsClient: GameWebSocketClient;
  private worldState: GameClientWorldState;
  private screenWidth: number;
  private screenHeight: number;

  // Column content texts
  private columnContainers: Container[] = [];
  private columnTexts: Text[] = [];
  private columnBgs: Graphics[] = [];
  private columnTitles: Text[] = [];

  // Guidance elements
  private hintText: Text;
  private nextStageBuildingsText: Text;
  private flashText: Text;
  private _flashTimer: number = 0;
  private _prevResearchActive: boolean = false;

  constructor(
    parentContainer: Container,
    screenWidth: number,
    screenHeight: number,
    wsClient: GameWebSocketClient,
    worldState: GameClientWorldState,
  ) {
    this.wsClient = wsClient;
    this.worldState = worldState;
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;

    this.container = new Container();
    this.container.visible = false;

    // Background
    this.bg = new Graphics();
    this.bg.eventMode = 'static';
    this.bg.on('pointerdown', (event) => { event.stopPropagation(); });
    this.container.addChild(this.bg);

    const titleStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 20,
      fill: GOLD_COLOR,
      fontWeight: 'bold',
    });

    const bodyStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 14,
      fill: TEXT_COLOR,
    });

    const smallStyle = new TextStyle({
      fontFamily: 'monospace',
      fontSize: 11,
      fill: DIM_COLOR,
    });

    // Title
    this.titleText = new Text({ text: '道法传承 — 技术树', style: titleStyle, resolution: 2, roundPixels: true });
    this.container.addChild(this.titleText);

    // Stone count
    this.stoneText = new Text({ text: '', style: bodyStyle, resolution: 2, roundPixels: true });
    this.container.addChild(this.stoneText);

    // Progress bar background
    this.progressBarBg = new Graphics();
    this.container.addChild(this.progressBarBg);

    // Progress bar fill
    this.progressBarFill = new Graphics();
    this.container.addChild(this.progressBarFill);

    // Progress text
    this.progressText = new Text({ text: '', style: smallStyle, resolution: 2, roundPixels: true });
    this.container.addChild(this.progressText);

    // Research button
    this.researchButton = new Container();
    this.researchButton.eventMode = 'static';
    this.researchButton.cursor = 'pointer';

    const btnBg = new Graphics();
    btnBg.roundRect(0, 0, 500, 32, 4);
    btnBg.fill({ color: 0x226622, alpha: 1 });
    btnBg.rect(0.5, 0.5, 499, 31);
    btnBg.stroke({ color: 0x44ff44, width: 1, alpha: 0.6 });
    this.researchButton.addChild(btnBg);

    this.researchButtonText = new Text({
      text: '开始研究',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 13, fill: ACCENT_COLOR }),
      resolution: 2,
      roundPixels: true,
    });
    this.researchButtonText.anchor.set(0.5, 0.5);
    this.researchButtonText.x = 250;
    this.researchButtonText.y = 16;
    this.researchButton.addChild(this.researchButtonText);

    this.researchButton.on('pointertap', () => this.startResearch());
    this.container.addChild(this.researchButton);

    // Create 3 columns
    for (let i = 0; i < 3; i++) {
      const colContainer = new Container();
      this.container.addChild(colContainer);
      this.columnContainers.push(colContainer);

      const colBg = new Graphics();
      colContainer.addChild(colBg);
      this.columnBgs.push(colBg);

      const colTitle = new Text({
        text: '',
        style: new TextStyle({ fontFamily: 'monospace', fontSize: 14, fontWeight: 'bold' }),
        resolution: 2,
        roundPixels: true,
      });
      colTitle.anchor.set(0.5, 0);
      colContainer.addChild(colTitle);
      this.columnTitles.push(colTitle);

      const colText = new Text({
        text: '',
        style: new TextStyle({ fontFamily: 'monospace', fontSize: 12, fill: TEXT_COLOR }),
        resolution: 2,
        roundPixels: true,
      });
      colContainer.addChild(colText);
      this.columnTexts.push(colText);
    }

    // Close hint
    const closeHint = new Text({
      text: '按 T 关闭',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 11, fill: 0x555555 }),
      resolution: 2,
      roundPixels: true,
    });
    this.container.addChild(closeHint);

    // Hint text (shown when no spirit stones)
    this.hintText = new Text({
      text: '',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 13, fill: 0xffaa44 }),
      resolution: 2,
      roundPixels: true,
    });
    this.container.addChild(this.hintText);

    // Next-stage buildings list
    this.nextStageBuildingsText = new Text({
      text: '',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 12, fill: 0x88ccff }),
      resolution: 2,
      roundPixels: true,
    });
    this.container.addChild(this.nextStageBuildingsText);

    // Flash message for research complete
    this.flashText = new Text({
      text: '',
      style: new TextStyle({ fontFamily: 'monospace', fontSize: 15, fill: 0x44ff44, fontWeight: 'bold' }),
      resolution: 2,
      roundPixels: true,
    });
    this.flashText.anchor.set(0.5, 0.5);
    this.container.addChild(this.flashText);

    parentContainer.addChild(this.container);
    this.drawLayout();
  }

  private drawLayout(): void {
    const cx = (this.screenWidth - PANEL_WIDTH) / 2;
    const cy = (this.screenHeight - PANEL_HEIGHT) / 2;

    this.bg.clear();
    this.bg.roundRect(cx, cy, PANEL_WIDTH, PANEL_HEIGHT, 8);
    this.bg.fill({ color: BG_COLOR, alpha: BG_ALPHA });
    this.bg.roundRect(cx + 1, cy + 1, PANEL_WIDTH - 2, PANEL_HEIGHT - 2, 7);
    this.bg.stroke({ color: 0x444444, width: 1, alpha: 0.6 });

    // Title
    this.titleText.x = cx + 20;
    this.titleText.y = cy + 15;

    // Stone count
    this.stoneText.x = cx + PANEL_WIDTH - 200;
    this.stoneText.y = cy + 18;

    // Columns
    for (let i = 0; i < 3; i++) {
      const colX = cx + 20 + i * (COLUMN_WIDTH + 10);
      const colY = cy + 55;
      this.columnContainers[i].x = colX;
      this.columnContainers[i].y = colY;

      this.columnBgs[i].clear();
      this.columnBgs[i].roundRect(0, 0, COLUMN_WIDTH, 280, 4);
      this.columnBgs[i].fill({ color: 0x111122, alpha: 0.6 });

      this.columnTitles[i].x = COLUMN_WIDTH / 2;
      this.columnTitles[i].y = 6;
    }

    // Progress bar
    this.progressBarBg.clear();
    this.progressBarBg.rect(cx + 20, cy + 355, PANEL_WIDTH - 40, 14);
    this.progressBarBg.fill({ color: 0x333333, alpha: 1 });
    this.progressBarFill.x = cx + 20;
    this.progressBarFill.y = cy + 355;
    this.progressText.x = cx + 20;
    this.progressText.y = cy + 374;

    // Research button
    this.researchButton.x = cx + 20;
    this.researchButton.y = cy + 400;

    // Hint text — below research button
    this.hintText.x = cx + 20;
    this.hintText.y = cy + 445;

    // Next-stage buildings list — below hint / research button
    this.nextStageBuildingsText.x = cx + 20;
    this.nextStageBuildingsText.y = cy + 475;

    // Flash message — centered on panel
    this.flashText.x = cx + PANEL_WIDTH / 2;
    this.flashText.y = cy + PANEL_HEIGHT / 2;

    // Close hint
    const children = this.container.children;
    for (const child of children) {
      if (child instanceof Text && (child as Text).text === '按 T 关闭') {
        child.x = cx + PANEL_WIDTH - 80;
        child.y = cy + PANEL_HEIGHT - 25;
      }
    }
  }

  handleResize(newWidth: number, newHeight: number): void {
    this.screenWidth = newWidth;
    this.screenHeight = newHeight;
    if (this.visible) {
      this.drawLayout();
    }
  }

  toggle(): void {
    this.visible = !this.visible;
    this.container.visible = this.visible;
    if (this.visible) {
      this.drawLayout();
      this.refreshContent();
    }
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  refreshContent(): void {
    const ws = this.worldState;
    const currentStage = ws.techStage;
    const currentStageLabel = TECH_LABELS[currentStage] ?? currentStage;

    this.stoneText.text = `灵石: ${Math.floor(ws.spiritStone)}`;
    this.stoneText.style.fill = GOLD_COLOR;

    // Show hint when player has 0 spirit stones
    if (ws.spiritStone <= 0) {
      this.hintText.text = '💡 建造灵根产生灵气 → 建造采集阵转换灵石';
      this.hintText.visible = true;
    } else {
      this.hintText.visible = false;
    }

    // Determine next stage
    const currentIdx = TECH_ORDER.indexOf(currentStage);

    // --- Render 3 columns ---
    for (let i = 0; i < 3; i++) {
      const stageKey = TECH_ORDER[i];
      const stageLabel = TECH_LABELS[stageKey] ?? stageKey;
      const isCurrent = i === currentIdx;
      const isPast = i < currentIdx;

      this.columnTitles[i].text = stageLabel;
      this.columnTitles[i].style.fill = isCurrent ? ACCENT_COLOR : isPast ? TEXT_COLOR : 0x555555;

      // Column background
      this.columnBgs[i].clear();
      this.columnBgs[i].roundRect(0, 0, COLUMN_WIDTH, 280, 4);
      if (isCurrent) {
        this.columnBgs[i].fill({ color: HIGHLIGHT_GREEN, alpha: 0.6 });
        this.columnBgs[i].roundRect(0.5, 0.5, COLUMN_WIDTH - 0.5, 279.5, 3.5);
        this.columnBgs[i].stroke({ color: CURRENT_BORDER, width: 1.5, alpha: 0.8 });
      } else if (isPast) {
        this.columnBgs[i].fill({ color: 0x111122, alpha: 0.6 });
        this.columnBgs[i].roundRect(0.5, 0.5, COLUMN_WIDTH - 0.5, 279.5, 3.5);
        this.columnBgs[i].stroke({ color: 0x336633, width: 1, alpha: 0.5 });
      } else {
        this.columnBgs[i].fill({ color: 0x0a0a0a, alpha: 0.6 });
      }

      // Building list
      const buildings = STAGE_BUILDINGS[stageKey] ?? [];
      const lines: string[] = [];
      for (const bk of buildings) {
        const label = BUILD_LABELS[bk] ?? bk;
        if (isPast || isCurrent) {
          lines.push(`  ✓ ${label}`);
        } else {
          lines.push(`  🔒 ${label}`);
        }
      }
      this.columnTexts[i].text = lines.join('\n') || '  —';
      this.columnTexts[i].y = 28;
    }

    // --- Progress bar & research button ---
    let nextStage: string | null = null;
    if (currentIdx >= 0 && currentIdx < TECH_ORDER.length - 1) {
      nextStage = TECH_ORDER[currentIdx + 1];
    }

    if (nextStage) {
      const nextLabel = TECH_LABELS[nextStage] ?? nextStage;
      const cost = RESEARCH_COST[nextStage] ?? 0;
      const canAfford = ws.spiritStone >= cost;

      this.researchButton.visible = !ws.researchState?.active;
      this.researchButton.alpha = canAfford ? 1 : 0.4;
      // Button text: "研究{stage} ({cost}灵石) [当前: {current}灵石]"
      this.researchButtonText.text = canAfford
        ? `研究${nextLabel} (${cost}灵石) [当前: ${Math.floor(ws.spiritStone)}灵石]`
        : `灵石不足 — 研究${nextLabel}需${cost}灵石 [当前: ${Math.floor(ws.spiritStone)}灵石]`;

      // Show next-stage buildings under the research button
      const nextBuildings = STAGE_BUILDINGS[nextStage] ?? [];
      if (nextBuildings.length > 0 && !ws.researchState?.active) {
        const names = nextBuildings.map(bk => BUILD_LABELS[bk] ?? bk);
        let buildingList = names.join('、');
        // Truncate if too long for panel width (monospace ~12px per CJK char, ~6px per ASCII)
        const maxWidth = PANEL_WIDTH - 60; // leave 30px margin on each side
        const estimatedWidth = buildingList.length * 7; // rough avg char width at 12px
        if (estimatedWidth > maxWidth) {
          const maxChars = Math.floor(maxWidth / 7);
          buildingList = buildingList.substring(0, maxChars - 3) + '...';
        }
        this.nextStageBuildingsText.text = `🔓 下一阶段解锁: ${buildingList}`;
        this.nextStageBuildingsText.visible = true;
      } else {
        this.nextStageBuildingsText.visible = false;
      }

      // Progress bar
      if (ws.researchState?.active && ws.researchState.targetStage === nextStage) {
        const ratio = ws.researchState.duration > 0
          ? Math.min(1, ws.researchState.progress / ws.researchState.duration)
          : 0;
        const barW = (PANEL_WIDTH - 44) * ratio;

        this.progressBarFill.clear();
        this.progressBarFill.rect(0, 0, barW, 14);
        this.progressBarFill.fill({ color: 0x2288ff, alpha: 1 });

        this.progressText.text = `研究 ${nextLabel} 进度: ${Math.floor(ratio * 100)}%`;
        this.progressText.visible = true;
        this.progressBarFill.visible = true;

        // Track that we're currently researching
        this._prevResearchActive = true;
      } else {
        // Detect research just completed
        if (this._prevResearchActive && !ws.researchState?.active) {
          this.showFlash('✅ 研究完成！新建筑已解锁');
          this._prevResearchActive = false;
        }
        this.progressBarFill.clear();
        this.progressBarFill.visible = false;
        this.progressText.visible = false;
      }
    } else {
      this.researchButton.visible = false;
      this.progressBarFill.visible = false;
      this.progressText.visible = false;
      this.nextStageBuildingsText.visible = false;
    }
  }

  /** Show a flash message that fades after a few seconds */
  private showFlash(msg: string): void {
    this.flashText.text = msg;
    this.flashText.visible = true;
    this.flashText.alpha = 1;
    this._flashTimer = 3.0; // 3 seconds
  }

  /** Call every frame to update flash timer. dt is in seconds. */
  updateFlash(dt: number): void {
    if (this._flashTimer > 0) {
      this._flashTimer -= dt;
      if (this._flashTimer <= 0) {
        this.flashText.visible = false;
        this._flashTimer = 0;
      } else if (this._flashTimer < 0.5) {
        // Fade out in last 0.5s
        this.flashText.alpha = this._flashTimer / 0.5;
      }
    }
  }

  private startResearch(): void {
    const currentIdx = TECH_ORDER.indexOf(this.worldState.techStage);
    if (currentIdx >= 0 && currentIdx < TECH_ORDER.length - 1) {
      const nextStage = TECH_ORDER[currentIdx + 1];
      const cost = RESEARCH_COST[nextStage] ?? 0;
      if (this.worldState.spiritStone >= cost) {
        this.wsClient.send({ type: 'StartResearch', target_stage: nextStage });
        console.info(`StartResearch: ${nextStage}`);
      }
    }
  }
}
